import React from "react";
import { Link, useParams } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { VideosLayout } from "@/components/videos/VideosLayout";
import { VideoCard, VideoGrid } from "@/components/videos/VideoCard";
import { useVideosData } from "@/hooks/useVideosData";
import { Lightbulb, Sparkles, LayoutGrid, ArrowRight } from "lucide-react";

export const VideosCategoryDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { videos, categories, collections, loading, error, refetch } =
    useVideosData();

  const currentCategory = categories.find((c) => c.slug === slug) || {
    slug: slug || "",
    name:
      slug === "before-after"
        ? "Before & After"
        : slug
          ? slug.charAt(0).toUpperCase() + slug.slice(1)
          : "Category",
    description: "Browse videos in this category.",
    count: 0,
    comingSoon: false,
  };

  const categoryVideos = videos.filter((v) => {
    if (slug === "before-after") {
      return (
        v.category?.toLowerCase() === "before-after" ||
        v.category?.toLowerCase() === "before & after" ||
        v.title.toLowerCase().includes("before")
      );
    }
    return v.category?.toLowerCase() === slug?.toLowerCase();
  });

  const categoryCollections = collections.filter(
    (c) => c.category?.toLowerCase() === slug?.toLowerCase(),
  );

  const categoryIcons: Record<string, any> = {
    tips: Lightbulb,
    "before-after": Sparkles,
    setups: LayoutGrid,
  };

  const Icon = categoryIcons[slug || ""] || LayoutGrid;

  const pageTitle = `${currentCategory.name} Videos | JNN Homes Northern Virginia`;
  const canonicalUrl = `https://www.jnnhomes.com/videos/c/${slug}`;

  return (
    <VideosLayout loading={loading} error={error} onRetry={refetch}>
      <SEOHead
        title={pageTitle}
        description={currentCategory.description}
        canonical={canonicalUrl}
      />

      <div className="space-y-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-slate-500 font-medium">
          <Link to="/videos" className="hover:text-[#9A5207]">
            Videos
          </Link>
          <span>›</span>
          <span className="text-slate-500">Categories</span>
          <span>›</span>
          <span className="text-slate-900 font-semibold">
            {currentCategory.name}
          </span>
        </nav>

        {/* Category Header */}
        <div className="p-6 rounded-2xl bg-slate-50 border border-slate-200 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="p-3.5 rounded-xl bg-[#E8850F]/14 text-[#9A5207] shrink-0">
            <Icon className="w-7 h-7" />
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-extrabold text-slate-900">
                {currentCategory.name}
              </h1>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-200 text-slate-700">
                {categoryVideos.length} videos
              </span>
            </div>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">
              {currentCategory.description}
            </p>
          </div>
        </div>

        {/* Category Collections if any */}
        {categoryCollections.length > 0 && (
          <section className="space-y-4">
            <h2 className="text-base font-bold text-slate-900">
              Featured playlists in this category
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categoryCollections.map((col) => (
                <Link
                  key={col.slug}
                  to={`/videos/course/${col.slug}`}
                  className="p-4 rounded-xl border border-slate-200 bg-white hover:border-[#E8850F] transition-all group space-y-2 shadow-xs"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-[#9A5207]">
                      {col.lessonCount} lessons
                    </span>
                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#9A5207] group-hover:translate-x-1 transition-all" />
                  </div>
                  <h3 className="font-bold text-base text-slate-900 group-hover:text-[#9A5207] transition-colors">
                    {col.title}
                  </h3>
                  <p className="text-xs text-slate-600 line-clamp-2">
                    {col.description}
                  </p>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Videos Grid / Empty State */}
        <section className="space-y-4">
          <h2 className="text-base font-bold text-slate-900">
            All {currentCategory.name} videos
          </h2>

          {categoryVideos.length === 0 ? (
            <div className="p-12 rounded-2xl bg-slate-50 border border-slate-200 text-center space-y-4">
              <h3 className="text-lg font-bold text-slate-900">Coming soon</h3>
              <p className="text-sm text-slate-600 max-w-md mx-auto">
                We're currently filming and editing more videos for{" "}
                {currentCategory.name}. Subscribe to our channel to be notified
                when they drop!
              </p>
              <a
                href="https://www.youtube.com/@JNNHomes?sub_confirmation=1"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-900 text-white font-bold text-xs hover:bg-slate-800 transition-colors shadow-sm"
              >
                Subscribe on YouTube
              </a>
            </div>
          ) : (
            <VideoGrid>
              {categoryVideos.map((video) => {
                const parentCol = collections.find((c) =>
                  c.lessons.includes(video.id),
                );
                return (
                  <VideoCard
                    key={video.id}
                    video={video}
                    collectionSlug={parentCol?.slug}
                  />
                );
              })}
            </VideoGrid>
          )}
        </section>
      </div>
    </VideosLayout>
  );
};
